import { FormEvent, useState } from 'react';
import api from '../services/api';
import { errorMessage } from '../lib/http';
import { FEE_CURRENCIES, PAYMENT_STATUS, formatDate, formatDateTime, formatMoney } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, FIELD, FIELD_STYLE, LABEL } from '../lib/ui';
import type { FeeSchedule, FundingRecord, Payment } from '../types/workspace';
import { Card, EmptyState, Notice, Spinner, StatusPill } from './ui';
import Select from './Select';
import type { Lead } from './PartnerLeads';

const today = () => new Date().toISOString().slice(0, 10);

function PaymentRow({ companyId, payment, onChanged, onError }: { companyId: string; payment: Payment; onChanged: (m?: string) => void; onError: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const st = PAYMENT_STATUS[payment.status];
  const act = async (action: 'MARK_PAID' | 'CANCEL') => {
    const reference = action === 'MARK_PAID' ? window.prompt('Bank transfer / UTR reference (optional)') : null;
    if (action === 'MARK_PAID' && reference === null) return;
    if (action === 'CANCEL' && !window.confirm(`Waive ${payment.description} (${formatMoney(payment.amount, payment.currency)})?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/companies/${companyId}/payments/${payment.id}`, { action, reference: reference || null });
      onChanged(action === 'MARK_PAID' ? 'Payment marked as paid.' : 'Payment waived.');
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#6b0030]">
          {payment.kind === 'RATING_FEE' ? 'Rating fee' : 'Success fee'} · {formatMoney(payment.amount, payment.currency)}
        </p>
        <p className="truncate text-[11px] text-[#c084a0]">
          {payment.description} · {payment.mode === 'DOMESTIC' ? 'India (all methods)' : 'International card'}
          {payment.status === 'PAID' ? ` · paid ${formatDateTime(payment.paidAt)}${payment.providerPaymentId ? ` · ${payment.providerPaymentId}` : ''}` : ''}
          {payment.failureReason && payment.status !== 'PAID' ? ` · last attempt: ${payment.failureReason}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <StatusPill label={st?.label || payment.status} tone={st?.tone || 'slate'} />
        {payment.status !== 'PAID' && (
          <>
            <button type="button" disabled={busy} onClick={() => void act('MARK_PAID')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
              Mark paid
            </button>
            <button type="button" disabled={busy} onClick={() => void act('CANCEL')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
              Waive
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/** Funded step (Consult): after partner and Micus amounts settle, record funding amount + date — sets company to FUNDED and raises the success fee. */
export default function FundingPanel({
  companyId,
  fundings,
  payments,
  leads,
  feeSchedule,
  onChanged,
  onError,
}: {
  companyId: string;
  fundings: FundingRecord[];
  payments: Payment[];
  leads: Lead[];
  feeSchedule: FeeSchedule | null;
  onChanged: (message?: string) => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    submissionId: '',
    partnerName: '',
    amount: '',
    currency: feeSchedule?.currency || 'INR',
    fundedAt: today(),
    instrument: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);

  const amountMinor = Math.round(Number(form.amount || 0) * 100);
  const previewFee = feeSchedule ? Math.max(Math.round((amountMinor * feeSchedule.successFeePercent) / 100), feeSchedule.minSuccessFee) : 0;

  const pickLead = (id: string) => {
    const lead = leads.find((l) => l.id === id);
    setForm((f) => ({ ...f, submissionId: id, partnerName: lead ? lead.partner.firm || lead.partner.name : f.partnerName }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/companies/${companyId}/fundings`, {
        ...form,
        submissionId: form.submissionId || null,
        amount: Number(form.amount),
        instrument: form.instrument || null,
        note: form.note || null,
      });
      setForm((f) => ({ ...f, submissionId: '', partnerName: '', amount: '', instrument: '', note: '' }));
      onChanged('Funding recorded — the company moved to Funded and the success fee is now due in their Payments.');
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f: FundingRecord) => {
    if (!window.confirm(`Remove the ${formatMoney(f.amount, f.currency)} funding from ${f.partnerName}? Its unpaid success fee is removed too.`)) return;
    try {
      await api.delete(`/admin/companies/${companyId}/fundings/${f.id}`);
      onChanged('Funding record removed.');
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
      <form onSubmit={submit} className="lg:sticky lg:top-24 lg:self-start">
        <Card
          title="Mark as Funded"
          subtitle={
            feeSchedule
              ? `Enter the settled funding amount and date. Company moves to Funded and a ${feeSchedule.successFeePercent}% Micus success fee is raised${feeSchedule.minSuccessFee ? ` (min ${formatMoney(feeSchedule.minSuccessFee, feeSchedule.currency)})` : ''}.`
              : 'Enter the settled funding amount and date to mark the company Funded. No fee schedule for this country — no success fee will be raised.'
          }
        >
          {leads.length > 0 && (
            <>
              <label className={LABEL}>Partner lead</label>
              <Select
                size="sm"
                value={form.submissionId}
                onChange={pickLead}
                placeholder="Not linked to a lead"
                options={leads.map((l) => ({ value: l.id, label: l.partner.firm || l.partner.name, description: l.partner.name }))}
                aria-label="Partner lead"
              />
            </>
          )}
          <label className={`${LABEL} mt-3`}>Partner / lender name</label>
          <input required value={form.partnerName} onChange={(e) => setForm({ ...form, partnerName: e.target.value })} className={FIELD} style={FIELD_STYLE} />
          <div className="mt-3 grid grid-cols-[1fr_110px] gap-2">
            <div>
              <label className={LABEL}>Disbursed amount</label>
              <input required type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={FIELD} style={FIELD_STYLE} placeholder="50000000" />
            </div>
            <div>
              <label className={LABEL}>Currency</label>
              <Select size="sm" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={FEE_CURRENCIES.map((c) => ({ value: c, label: c }))} aria-label="Currency" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <label className={LABEL}>Disbursed on</label>
              <input required type="date" value={form.fundedAt} onChange={(e) => setForm({ ...form, fundedAt: e.target.value })} className={FIELD} style={FIELD_STYLE} />
            </div>
            <div>
              <label className={LABEL}>Instrument</label>
              <input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className={FIELD} style={FIELD_STYLE} placeholder="Term loan" />
            </div>
          </div>
          <label className={`${LABEL} mt-3`}>Note</label>
          <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${FIELD} resize-y`} style={FIELD_STYLE} />
          {amountMinor > 0 && (
            <div className="mt-3">
              <Notice tone="pink">
                Micus success fee: <strong>{formatMoney(previewFee, form.currency)}</strong>
                {feeSchedule ? ` (${feeSchedule.successFeePercent}% of ${formatMoney(amountMinor, form.currency)})` : ''}
              </Notice>
            </div>
          )}
          <button type="submit" disabled={saving} className={`${BTN_PRIMARY} mt-4 w-full py-3`} style={BTN_PRIMARY_STYLE}>
            {saving && <Spinner />} Record funding
          </button>
        </Card>
      </form>

      <div className="space-y-4">
        <Card title="Fundings">
          {fundings.length === 0 ? (
            <EmptyState title="Not funded yet" body="Record a disbursement when a partner releases funds." />
          ) : (
            <ul className="divide-y divide-[#fce7f3]">
              {fundings.map((f) => (
                <li key={f.id} className="flex flex-wrap items-start justify-between gap-2 py-2.5">
                  <div>
                    <p className="text-sm font-bold text-[#065f46]">{formatMoney(f.amount, f.currency)}</p>
                    <p className="text-[11px] text-[#c084a0]">
                      {f.partnerName}
                      {f.instrument ? ` · ${f.instrument}` : ''} · {formatDate(f.fundedAt)} · by {f.recordedBy?.name || f.recordedBy?.email || '—'}
                    </p>
                    <p className="text-[11px] text-[#6b3a52]">
                      Fee {f.successFeePercent}% = {formatMoney(f.successFee, f.currency)}
                      {f.payment ? ` · ${PAYMENT_STATUS[f.payment.status]?.label}` : ''}
                    </p>
                  </div>
                  {f.payment?.status !== 'PAID' && (
                    <button type="button" onClick={() => void remove(f)} className="text-[11px] font-semibold text-[#be123c] hover:underline">
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Payments" subtitle="Razorpay payments confirm automatically. Use Mark paid for bank transfers.">
          {payments.length === 0 ? (
            <p className="text-sm text-[#c084a0]">No fees raised for this company.</p>
          ) : (
            <ul className="divide-y divide-[#fce7f3]">
              {payments.map((p) => (
                <PaymentRow key={p.id} companyId={companyId} payment={p} onChanged={onChanged} onError={onError} />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
